import type {
  GA4ImpressionItemsConfig,
  SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type {
  APIBookingDraft,
  APIBookingDraftLuggageService,
  APIBookingDraftLuggageServiceOption,
  APIBookingDraftRoom,
  APIBookingDraftServiceLuggageFlightDefinition,
} from '@/scopes/checkout/infrastructure/api/types'
import { isEmpty, isNil, values } from 'ramda'

import {
  type CheckoutLuggageService,
  type CheckoutServiceOption,
  type ServiceMapper,
  ServiceType,
} from '../../../domain/types'
import {
  getGA4CategoryByServiceType,
  mapDefaultSelectedItemToGA4,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceOptionMapper?: ServiceOptionMapper
  serviceBannersMapper?: ServiceBannersMapper
  serviceType: ServiceType
}

export const createLuggageServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutLuggageService> = (
  dependencies: Dependencies,
) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceOptionMapper = createServiceOptionMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
    serviceType,
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftLuggageService

    if (!service)
      return null

    const { aName, options, hasSpecialLuggage, isOneClickLuggageReady, oneClickLuggageOptions } = service

    const outboundOptions = options.filter(({ direction }) => direction?.toLowerCase() === 'outbound')
    const inboundOptions = options.filter(({ direction }) => direction?.toLowerCase() === 'inbound')
    const participants
      = bookingDraft?.participants.reduce(
        (acc, participant) => ({
          ...acc,
          [participant.id.toString()]: {
            id: participant.id.toString(),
            name: participant.name || participant.alternativeName?.toLowerCase() || '',
            roomId: participant.roomId.toString(),
            selectedValueOutbound:
              options
                .filter(({ isSelected }) => isSelected)
                .filter(({ direction }) => direction?.toLowerCase() === 'outbound')
                .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id))
                ?.code || null,
            selectedValueInbound:
              options
                .filter(({ isSelected }) => isSelected)
                .filter(({ direction }) => direction?.toLowerCase() === 'inbound')
                .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id))
                ?.code || null,
            availableOptions: options
              .filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map(({ code }) => code),
            availableOneClickOptions: oneClickLuggageOptions
              ?.filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map(({ code }) => code),
          },
        }),
        {},
      ) ?? {}

    const anyParticipantHasSelectedValue = values(participants).some(
      ({ selectedValueOutbound }) => selectedValueOutbound,
    )
    const oneClickLuggageNoneOption = oneClickLuggageOptions?.find(({ isNoneOption }) => isNoneOption) || null
    const outboundOptionsFiltered
        = outboundOptions && outboundOptions.filter(({ isNoneOption }) => !isNoneOption).length > 0
          ? outboundOptions.reduce(
              (acc2: Record<string, CheckoutServiceOption>, option) => ({
                ...acc2,
                [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
              }),
              {},
            )
          : {}
    const inboundOptionsFiltered = inboundOptions && inboundOptions.filter(({ isNoneOption }) => !isNoneOption).length > 0
      ? inboundOptions.reduce(
          (acc2: Record<string, CheckoutServiceOption>, option) => ({
            ...acc2,
            [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
          }),
          {},
        )
      : {}
    const oneClickLuggageOptionsFiltered = isOneClickLuggageReady && oneClickLuggageOptions
      ? oneClickLuggageOptions
          .filter(({ isNoneOption }) => !isNoneOption)
          .reduce(
            (acc2: Record<string, CheckoutServiceOption>, option) => ({
              ...acc2,
              [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
            }),
            {},
          )
      : {}
    const luggageIsIncludedByDefault = isEmpty(outboundOptionsFiltered) && isEmpty(inboundOptionsFiltered) && isEmpty(oneClickLuggageOptionsFiltered)

    return {
      id: aName,
      isSelected: luggageIsIncludedByDefault || (anyParticipantHasSelectedValue ?? !isNil(anyParticipantHasSelectedValue)),
      type: ServiceType.Luggage,
      rooms: bookingDraft?.rooms.map(mapRoom) ?? [],
      participants,
      disableInboundSelection: service.sameOutboundInboundWeights ?? false,
      outboundOptions: outboundOptionsFiltered,
      inboundOptions: inboundOptionsFiltered,
      isOneClickLuggageReady: isOneClickLuggageReady ?? false,
      oneClickSelectedValue: oneClickLuggageOptions?.find(({ isSelected }) => isSelected)?.code || null,
      oneClickLuggageOptions: oneClickLuggageOptionsFiltered,
      oneClickLuggageNoneOption: oneClickLuggageNoneOption ? serviceOptionMapper.map(oneClickLuggageNoneOption, service, bookingDraft) : null,
      hasSpecialLuggage: hasSpecialLuggage ?? false,
      flightAirports: mapFlightAirports(service),
      flightsInformationBanner: mapFlightInformationBanner(service),
      banners: serviceBannersMapper.map(service),
    }
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map(participantId => participantId.toString()),
    }
  }

  function mapFlightAirports({ luggageDefinitionInformation }: APIBookingDraftLuggageService) {
    if (!luggageDefinitionInformation) {
      return undefined
    }
    const { outboundFlightDefinition, inboundFlightDefinition } = luggageDefinitionInformation
    let text = ''

    if (outboundFlightDefinition) {
      text += `${outboundFlightDefinition.airportNameDeparture} - ${outboundFlightDefinition.airportNameArrival}, ${outboundFlightDefinition.airlineName}`
    }
    if (inboundFlightDefinition) {
      text += ` | ${inboundFlightDefinition.airportNameDeparture} - ${inboundFlightDefinition.airportNameArrival}, ${inboundFlightDefinition.airlineName}`
    }
    return text
  }

  function mapFlightInformationBanner({ luggageDefinitionInformation }: APIBookingDraftLuggageService) {
    if (!luggageDefinitionInformation) {
      return undefined
    }
    const { title, description, outboundFlightDefinition, inboundFlightDefinition } = luggageDefinitionInformation

    const outboundFlight = mapFlightDefinition(outboundFlightDefinition)
    const inboundFlight = mapFlightDefinition(inboundFlightDefinition)

    return {
      ...(title && { title }),
      ...(description && { text: description }),
      ...(outboundFlight && { outboundFlight }),
      ...(inboundFlight && { inboundFlight }),
      type: 'info',
    }
  }

  function mapFlightDefinition(flightDefinition?: APIBookingDraftServiceLuggageFlightDefinition) {
    if (!flightDefinition) {
      return
    }
    const { shouldBeShown, title, handLuggage, checkedInLuggage } = flightDefinition

    if (!shouldBeShown) {
      return
    }

    return {
      title,
      handLuggage,
      checkedInLuggage,
    }
  }

  function mapSelection(service: CheckoutLuggageService) {
    if (!service) {
      return null
    }
    const { participants } = service
    if (participants && Object.values(participants).length === 0) {
      return null
    }
    const participantsPayload = Object.values(participants ?? {})
      .map(({ id, selectedValueOutbound, selectedValueInbound, availableOneClickOptions }) => {
        if (service.oneClickSelectedValue) {
          if (!availableOneClickOptions.includes(service.oneClickSelectedValue)) {
            return {
              participantId: id,
              value: null,
            }
          }
          return {
            participantId: id,
            value: service.oneClickSelectedValue,
          }
        }
        return [
          {
            participantId: id,
            value: selectedValueOutbound,
          },
          {
            participantId: id,
            value: selectedValueInbound,
          },
        ]
      })
      .flat()

    return [...participantsPayload, { hasSpecialLuggage: service.hasSpecialLuggage }]
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftLuggageService | undefined
    if (!service)
      return []

    const ga4Category = getGA4CategoryByServiceType(serviceType, service.aName)

    const mapOption = (option: APIBookingDraftLuggageServiceOption): ImpressionViewAncillariesItem => ({
      item_name: option.displayText,
      item_id: option.code,
      price: serviceOptionMapper.map(option, service, bookingDraft).price,
      item_category: `Ancillary > Additional cost > ${ga4Category}`,
      item_category2: ga4Category,
      item_list_name: 'checkout1',
      quantity: '1',
    })

    const mapOneClickOption = (option: APIBookingDraftLuggageServiceOption): ImpressionViewAncillariesItem => ({
      ...mapOption(option),
      quantity: String(option.appliesToParticipants.length * 2),
    })

    const regularItems = (service.options ?? [])
      .filter(option => !option.isNoneOption)
      .map(mapOption)

    const oneClickItems = service.isOneClickLuggageReady === true
      ? (service.oneClickLuggageOptions ?? []).filter(option => !option.isNoneOption).map(mapOneClickOption)
      : []

    const luggageItems = [...oneClickItems, ...regularItems]

    if (!config?.showSpecialLuggage)
      return luggageItems

    const specialLuggageItem = mapSpecialLuggageItem()
    return [...luggageItems, specialLuggageItem]
  }

  function mapSelectedItemToGA4(service: CheckoutLuggageService, step: number): ImpressionViewAncillariesItem[] {
    const specialLuggageItems = service.hasSpecialLuggage
      ? [{ ...mapSpecialLuggageItem(), item_list_name: `checkout${step}` }]
      : []

    if (service.oneClickSelectedValue) {
      const option = service.oneClickLuggageOptions[service.oneClickSelectedValue]

      if (!option)
        return specialLuggageItems

      return [
        ...mapDefaultSelectedItemToGA4(
          [{
            label: option.label,
            value: option.value,
            price: option.price,
            rawPrice: option.rawPrice,
            badge: option.badge,
            quantity: String(values(service.participants ?? {}).length * 2),
          }],
          {
            serviceType,
            serviceId: service.id,
            step,
          },
        ).map(item => ({
          ...item,
          item_category: 'Ancillary > Additional cost > Both',
          item_category2: 'Both',
        })),
        ...specialLuggageItems,
      ]
    }

    const inputs: SelectedItemInput[] = []
    const categories: string[] = []
    const category2s: string[] = []
    const seenItemIds = new Set<string>()

    values(service.participants ?? {}).forEach((participant) => {
      [
        {
          selectedValue: participant.selectedValueOutbound,
          option: service.outboundOptions[participant.selectedValueOutbound ?? ''],
          direction: 'Outbound',
        },
        {
          selectedValue: participant.selectedValueInbound,
          option: service.inboundOptions[participant.selectedValueInbound ?? ''],
          direction: 'Inbound',
        },
      ].forEach(({ selectedValue, option, direction }) => {
        if (!selectedValue || !option || seenItemIds.has(option.value))
          return

        seenItemIds.add(option.value)
        inputs.push({
          label: option.label,
          value: option.value,
          price: option.price,
          rawPrice: option.rawPrice,
          badge: option.badge,
        })
        categories.push(`Ancillary > Additional cost > ${direction}`)
        category2s.push(direction)
      })
    })

    return [
      ...mapDefaultSelectedItemToGA4(inputs, {
        serviceType,
        serviceId: service.id,
        step,
      }).map((item, index) => ({
        ...item,
        item_category: categories[index],
        item_category2: category2s[index],
      })),
      ...specialLuggageItems,
    ]
  }

  function mapSpecialLuggageItem(): ImpressionViewAncillariesItem {
    const ga4Category = getGA4CategoryByServiceType(serviceType, 'luggage')

    return {
      item_name: 'Special Luggage',
      item_id: 'special_luggage',
      price: null,
      item_category: `Ancillary > Additional cost > ${ga4Category}`,
      item_category2: ga4Category,
      item_list_name: 'checkout1',
      quantity: '1',
    }
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
